import { useMediaQuery } from '@mantine/hooks'
import { useIsFromPreNotice } from 'app/school/utils/scrollNavHook'
import { usePathname } from 'next/navigation'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link as ScrollLink, scroller, scrollSpy } from 'react-scroll'

export const schoolNavList = [
  {
    href: 'curriculum',
    name: '커리큘럼',
  },
  {
    href: 'project',
    name: '프로젝트',
  },
  {
    href: 'teacher',
    name: '강사진',
  },
  {
    href: 'support',
    name: '취업지원',
  },
  {
    href: 'condition',
    name: '모집조건',
  },
]

interface IScrollNavProps {
  schoolNavList: { href: string; name: string }[]
}

export const ScrollNav = ({ schoolNavList }: IScrollNavProps) => {
  const [activeKey, setActiveKey] = useState<string>(schoolNavList[0].href)
  const [manualActiveKey, setManualActiveKey] = useState<string | null>(null)
  const isManualScrollRef = useRef<boolean>(false)

  const isMobile = useMediaQuery('(max-width: 1023px)')
  const isFromPreNotice = useIsFromPreNotice()
  const observerRef = useRef<IntersectionObserver | null>(null)

  const getOffset = () => (isFromPreNotice ? (isMobile ? -46 : -55) : isMobile ? -100 : -115)

  const onChangeKey = useCallback(
    (key: string) => {
      if (key === activeKey) return

      setActiveKey(key)
      setManualActiveKey(key)
      isManualScrollRef.current = true

      setTimeout(() => {
        isManualScrollRef.current = false
        setManualActiveKey(null)
      }, 800)

      setTimeout(() => {
        scroller.scrollTo(key, {
          duration: 0,
          smooth: 'easeInOutQuart',
          offset: getOffset(),
          containerId: isFromPreNotice ? 'pre-notice-drawer' : undefined,
        })
      }, 600)
    },
    [activeKey, isFromPreNotice, isMobile],
  )

  useEffect(() => {
    if (!isFromPreNotice) return

    const container = document.getElementById('pre-notice-drawer') ?? undefined

    const options: IntersectionObserverInit = {
      root: container ?? null,
      threshold: 0.3,
    }

    observerRef.current = new IntersectionObserver(entries => {
      if (isManualScrollRef.current) return

      const visibleEntry = entries.find(entry => entry.isIntersecting)
      if (visibleEntry?.target?.id) {
        setActiveKey(visibleEntry.target.id)
      }
    }, options)

    schoolNavList.forEach(item => {
      const section = document.getElementById(item.href)
      if (section) observerRef.current?.observe(section)
    })

    return () => {
      observerRef.current?.disconnect()
    }
  }, [isFromPreNotice])

  return (
    <ul
      className={`sticky ${isFromPreNotice ? '!top-0' : 'top-[54px]'} z-75 flex items-center justify-center gap-x-6 border-b border-[#404040] bg-gray-900 lg:top-[62px] lg:gap-x-6`}
    >
      {schoolNavList.map(navItem => {
        const key = navItem.href
        const isActive = manualActiveKey ? manualActiveKey === key : activeKey === key

        return (
          <li className="cursor-pointer text-center" key={navItem.name}>
            <ScrollLink
              to={key}
              smooth="easeInOutCubic"
              duration={400}
              offset={getOffset()}
              containerId={isFromPreNotice ? 'pre-notice-drawer' : undefined}
              onClick={() => onChangeKey(key)}
              {...(!isFromPreNotice && {
                spy: true,
                onSetActive: () => {
                  if (isManualScrollRef.current) return
                  if (activeKey !== key) setActiveKey(key)
                },
              })}
            >
              <p
                className={`flex h-[47px] w-[50px] items-center justify-center text-sm lg:h-14 lg:w-[120px] lg:text-base ${
                  isActive
                    ? 'border-b-2 border-yellow-450 font-semibold text-yellow-450'
                    : 'text-label-assistive'
                }`}
              >
                {navItem.name}
              </p>
            </ScrollLink>
          </li>
        )
      })}
    </ul>
  )
}

export default ScrollNav
